/**
 * Reading a roster snapshot the way the database reads it.
 *
 * `compute_shift_segments` turns slot assignments into shifts; the rule
 * evaluators judge those shifts. A fix has to reason about the same shifts, so
 * the grouping here mirrors that SQL rather than inventing its own.
 */

import type { BreakSlot, DutySlot } from '../dutyBoardService';
import type {
  GuardShift,
  RosterRule,
  RosterRuleViolation,
  RosterSnapshot,
  TimeWindow,
} from './types';

export const ROSTER_SNAP_MINUTES = 15;
/** Breaks live on one roster day; `break_slots.end_minute` cannot pass midnight. */
export const BREAK_DAY_END_MINUTE = 1440;

export const snapUp = (minute: number, step = ROSTER_SNAP_MINUTES): number => Math.ceil(minute / step) * step;
export const snapDown = (minute: number, step = ROSTER_SNAP_MINUTES): number => Math.floor(minute / step) * step;

export const windowMinutes = (window: TimeWindow): number => Math.max(0, window.endMinute - window.startMinute);

export const overlaps = (a: TimeWindow, b: TimeWindow): boolean => (
  a.startMinute < b.endMinute && a.endMinute > b.startMinute
);

interface Segment {
  type: 'duty' | 'break';
  startMinute: number;
  endMinute: number;
  id: string;
  duty?: DutySlot;
  breakSlot?: BreakSlot;
}

/**
 * A guard's shifts on this roster. Segments that overlap or touch belong to one
 * shift; a gap starts the next — the same rule as `compute_shift_segments`.
 */
export function buildGuardShifts(snapshot: RosterSnapshot, guardId: string): GuardShift[] {
  let guardName = '';
  const segments: Segment[] = [];

  snapshot.slots.forEach((slot) => {
    const assignment = slot.assignments.find((a) => a.guard_id === guardId);
    if (!assignment) return;
    guardName = guardName || assignment.guard_name;
    segments.push({ type: 'duty', startMinute: slot.start_minute, endMinute: slot.end_minute, id: slot.id, duty: slot });
  });

  snapshot.breakSlots.forEach((breakSlot) => {
    const assignment = breakSlot.assignments.find((a) => a.guard_id === guardId);
    if (!assignment) return;
    guardName = guardName || assignment.guard_name;
    segments.push({
      type: 'break',
      startMinute: breakSlot.start_minute,
      endMinute: breakSlot.end_minute,
      id: breakSlot.id,
      breakSlot,
    });
  });

  // Same ordering as the SQL window: start, end, segment type, source id.
  segments.sort((a, b) => (
    a.startMinute - b.startMinute
    || a.endMinute - b.endMinute
    || a.type.localeCompare(b.type)
    || a.id.localeCompare(b.id)
  ));

  const shifts: GuardShift[] = [];
  let current: GuardShift | null = null;
  let maxEnd = -Infinity;

  segments.forEach((segment) => {
    if (!current || segment.startMinute > maxEnd) {
      current = {
        guardId,
        guardName,
        startMinute: segment.startMinute,
        endMinute: segment.endMinute,
        duties: [],
        breaks: [],
        dutyMinutes: 0,
        breakMinutes: 0,
      };
      shifts.push(current);
    }

    const length = segment.endMinute - segment.startMinute;
    if (segment.duty) {
      current.duties.push(segment.duty);
      current.dutyMinutes += length;
    }
    if (segment.breakSlot) {
      current.breaks.push(segment.breakSlot);
      current.breakMinutes += length;
    }
    current.startMinute = Math.min(current.startMinute, segment.startMinute);
    current.endMinute = Math.max(current.endMinute, segment.endMinute);
    maxEnd = Math.max(maxEnd, segment.endMinute);
  });

  return shifts;
}

/** Every guard who holds a duty or break on the roster. */
export function rosterGuardIds(snapshot: RosterSnapshot): string[] {
  const ids = new Set<string>();
  snapshot.slots.forEach((slot) => slot.assignments.forEach((a) => ids.add(a.guard_id)));
  snapshot.breakSlots.forEach((slot) => slot.assignments.forEach((a) => ids.add(a.guard_id)));
  return Array.from(ids);
}

/**
 * The shift on this roster a breach is about. Violations carry the source slot
 * ids of their shift, which stay stable when the database rebuilds `shifts`.
 * Null when that shift lives on another site's roster.
 */
export function findViolationShift(violation: RosterRuleViolation, snapshot: RosterSnapshot): GuardShift | null {
  if (!violation.guard_id) return null;
  const shifts = buildGuardShifts(snapshot, violation.guard_id);
  if (shifts.length === 0) return null;

  const sourceIds = new Set(violation.source_slot_ids);
  if (sourceIds.size === 0) {
    return shifts.reduce((longest, shift) => (shift.dutyMinutes > longest.dutyMinutes ? shift : longest));
  }

  return shifts.find((shift) => (
    shift.duties.some((slot) => sourceIds.has(slot.id))
    || shift.breaks.some((slot) => sourceIds.has(slot.id))
  )) ?? null;
}

/** The largest break the rule demands for this much duty — the evaluator's `max()` over matching tiers. */
export function requiredBreakMinutes(rule: RosterRule | null, dutyMinutes: number): number {
  if (!rule) return 0;
  return rule.breakRequirements.reduce((required, tier) => (
    dutyMinutes > tier.workMinutesOver ? Math.max(required, tier.minimumBreakMinutes) : required
  ), 0);
}

/**
 * Which side of the shift on this roster the short rest falls. A rest breach
 * names a previous and a next shift; the one on this roster is `shift_id`.
 */
export function restGapSide(violation: RosterRuleViolation, siteId: string): 'before' | 'after' {
  const details = violation.details;
  if (violation.shift_id && violation.shift_id === details.next_shift_id) return 'before';
  if (violation.shift_id && violation.shift_id === details.previous_shift_id) return 'after';
  return details.next_site_id === siteId ? 'before' : 'after';
}

/**
 * Identity of a breach across evaluations. Shift ids are not stable — the
 * database rebuilds `shifts` on every change — so a breach is its rule and
 * guard, plus which side of the shift a short rest sits.
 */
export function violationIdentity(violation: RosterRuleViolation): string {
  const guard = violation.guard_id ?? violation.guard_name ?? '';
  if (violation.rule_code === 'minimum_rest_between_shifts') {
    const details = violation.details;
    const side = violation.shift_id && violation.shift_id === details.previous_shift_id ? 'after' : 'before';
    return `${violation.rule_code}|${guard}|${side}`;
  }
  return `${violation.rule_code}|${guard}`;
}

/** Drops exact repeats of a breach; two different breaches of one rule both survive. */
export function uniqueViolations(violations: RosterRuleViolation[]): RosterRuleViolation[] {
  const seen = new Set<string>();
  return violations.filter((violation) => {
    const key = `${violationIdentity(violation)}|${violation.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function numberDetail(violation: RosterRuleViolation, key: string): number | null {
  const value = violation.details[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Licences the duties in a shift demand; a replacement must hold all of them. */
export function requiredLicenceIds(duties: DutySlot[]): string[] {
  return Array.from(new Set(
    duties.map((slot) => slot.required_licence_type_id).filter((id): id is string => Boolean(id)),
  ));
}
